"use client";

import { Loader, ArrowRight, ExternalLink } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function InquiryFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8 rounded-md bg-background">
        <p className="font-serif font-default text-2xl text-foreground mb-4">{successMessage}</p>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-6">
          A pest control specialist will contact you shortly to confirm your inspection.
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-[11px] uppercase tracking-widest rounded-md border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground transition-colors">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              01 / Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Your name" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_0_0_var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage className="font-mono text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              02 / Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_0_0_var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage className="font-mono text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              03 / Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="919-426-4852" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_0_0_var(--primary)] transition-colors" />
            </FormControl>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Required for scheduling your free inspection
            </p>
            <FormMessage className="font-mono text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              06 / Property Address
            </FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, Durham, NC" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_0_0_var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage className="font-mono text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              05 / Service Required
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-full border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary data-[placeholder]:text-muted-foreground/40">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border border-border rounded-md">
                <SelectItem value="Pest Control">General Pest Control</SelectItem>
                <SelectItem value="Mosquito Control">Mosquito Control</SelectItem>
                <SelectItem value="Commercial Pest Control">Commercial Pest Control</SelectItem>
                <SelectItem value="Other">Other / Emergency</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="font-mono text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              06 / Describe Your Situation
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about the pests you've seen, the location in your home, and any specific concerns..." {...field} rows={5} className="border-0 border-b border-border rounded-md bg-transparent px-0 py-2 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_0_0_var(--primary)] transition-colors resize-none min-h-[120px]" />
            </FormControl>
            <FormMessage className="font-mono text-[11px]" />
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 p-4 rounded-md">
          <p className="font-mono text-[11px] uppercase tracking-widest text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-6">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto rounded-md bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground px-8 h-12 font-mono text-[11px] uppercase tracking-widest shadow-md transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing
            </> : <>
              Request Free Quote
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          We respond to every request promptly · Licensed & Insured Professionals
        </p>
      </div>
    </Form>;
}
export default function GetYourFreeQuote() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactInfo = [{
    label: "Headquarters",
    value: "Serving Durham, Raleigh & Triangle Area"
  }, {
    label: "Phone",
    value: "919-896-1953"
  }, {
    label: "Email",
    value: "attictobasement@gmail.com"
  }, {
    label: "Availability",
    value: "MON-FRI: 8 AM - 8 PM"
  }];
  return <section data-section-id="3673" id="get-your-free-quote" className="bg-background text-foreground pt-32 md:pt-[200px] pb-24 md:pb-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-32 lg:self-start transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-[11px] uppercase tracking-widest text-primary mb-8">
              — Contact / 01
            </p>
            <span className="font-mono text-[11px] uppercase tracking-widest text-primary mb-2 block">
              Protecting Homes. Protecting Families. It's Our Promise.
            </span>
            <h1 className="font-serif font-default text-4xl md:text-5xl lg:text-[72px] leading-[1.05] text-foreground tracking-tight">
              Secure your home from <span className="italic text-primary">attic</span> to basement.
            </h1>
            <p className="mt-8 text-foreground/70 text-[17px] leading-relaxed max-w-[420px] font-default">
              Professional pest defense is just a message away. Whether you're dealing with ants, rodents
              or mosquitoes, our team provides the precision treatment your property deserves.
            </p>

            <div className="mt-16 space-y-0">
              {contactInfo.map((info, i) => <div key={i} data-index={i} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-5 border-t border-border last:border-b">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-primary shrink-0 sm:w-24">
                    {info.label}
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-widest text-foreground/80">
                    {info.label === "Phone" ? <Link to="tel:9198961953" className="hover:text-primary transition-colors">
                        {info.value}
                      </Link> : info.label === "Email" ? <Link to="mailto:attictobasement@gmail.com" className="hover:text-primary transition-colors">
                        {info.value}
                      </Link> : info.value}
                  </span>
                </div>)}
            </div>

            <div className="mt-8">
              <Button asChild className="w-full sm:w-auto rounded-md bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground h-12 px-8 font-mono text-[11px] uppercase tracking-widest shadow-md transition-all">
                <Link href="https://client.gorilladesk.com/attictobasementpestcontrol" target="_blank" rel="noopener noreferrer">
                  Open Customer Portal Home
                  <ExternalLink className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <FormProvider formKey="free_quote_form_contact" formId="free_quote_form_contact" sectionName="get-your-free-quote" defaultValues={{
            name: "",
            email: "",
            phone: "",
            address: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {},
            address: {},
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Request Free Quote" successMessage="Thank you. Your request has been received." errorMessage="We were unable to submit your request. Please call 919-896-1953.">
              <InquiryFormFields />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}